import {AsyncPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, filter, Observable, startWith, Subscription, take} from 'rxjs';
import {OperatorOption} from 'src/app/core/model/operator-option';

import {Tracker, TrackerMetric} from 'src/app/core/model/tracker';
import {selectTrackerAutocompleteOptions, selectTrackers} from 'src/app/core/store/trackers/trackers.selector';
import {BOOLEAN_OPTIONS, OPERATOR_OPTIONS} from 'src/app/projects/shared/projects-constants';
import {TimezoneService} from 'src/app/services/timezone.service';
import {
  OphAutocompleteComponent,
  OphAutocompleteOption,
} from '../../design/oph-autocomplete/oph-autocomplete.component';
import {OphDurationInputComponent} from '../../design/oph-duration-input/oph-duration-input.component';
import {OphIconModule} from '../../design/oph-icon/oph-icon.module';
import {OphXButtonComponent} from '../../design/oph-x-button/oph-x-button.component';
import {UserHasRoleOptionDirective} from '../../directives/user-has-role-option.directive';
import {PipesModule} from '../../pipes/pipes.module';
import {TasksService} from '../../services/tasks.service';
import {RolePermissionOptions} from './../../../core/model/role';
import {TrackerMetricTypes} from './../../../trackers/shared/tracker-metric-types';
import {TrackerMetricOptionsToStringsPipe} from 'src/app/trackers/tracker-detail/metrics/tracker-metric-options-to-strings.pipe';

@Component({
  selector: 'task-trigger-selection',
  standalone: true,
  imports: [
    OphAutocompleteComponent,
    AsyncPipe,
    MatMenuModule,
    OphXButtonComponent,
    PipesModule,
    MatIconModule,
    MatProgressSpinnerModule,
    OphIconModule,
    OphDurationInputComponent,
    MatDatepickerModule,
    ReactiveFormsModule,
    UserHasRoleOptionDirective,
    TrackerMetricOptionsToStringsPipe,
  ],
  templateUrl: './task-trigger-selection.component.html',
  styleUrl: './task-trigger-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskTriggerSelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() triggerFormGroup!: FormGroup;
  @Input() hideXButton: boolean;
  @Input() ophAutocompleteStyle: Object;
  @Input() ophAutocompleteNewButtonLoading: boolean;
  @Input() error: boolean;
  @Input() enableNewTrackerButton: boolean;
  @Input() addNewButtonRolePermissionNeeded?: RolePermissionOptions;

  @Output() removeItem = new EventEmitter();
  @Output() newClick = new EventEmitter();

  trackers$: Observable<Tracker[]>;
  autocompleteOptions$: Observable<OphAutocompleteOption[]>;

  selectedTracker$ = new BehaviorSubject<Tracker>(null);
  selectedFilteredTrackerMetrics$ = new BehaviorSubject<TrackerMetric[]>([]);
  selectedMetric$ = new BehaviorSubject<TrackerMetric>(null);
  triggerComplete$ = new BehaviorSubject<boolean>(false);

  private timezoneService = inject(TimezoneService);
  TrackerMetricTypes = TrackerMetricTypes;
  operatorOptions: OperatorOption[] = OPERATOR_OPTIONS;
  booleanOptions = BOOLEAN_OPTIONS;
  dateMetric: TrackerMetric = {
    _id: 'date',
    name: 'Date',
    type: TrackerMetricTypes.Date,
    updateable: true,
  } as TrackerMetric;

  formSubscription: Subscription;

  defaultOphAutocompleteStyle = {
    height: '34px',
    border: 'none',
    background: 'transparent',
    padding: '0',
  };
  rolePermissionOptions = RolePermissionOptions;
  newButtonDisabled = false;

  constructor(
    private store$: Store,
    private tasksService: TasksService
  ) {}

  ngOnInit() {
    this.trackers$ = this.store$.pipe(select(selectTrackers));
    this.autocompleteOptions$ = this.store$.pipe(select(selectTrackerAutocompleteOptions));
    this.checkTrigger();
    this.formSubscription = this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['triggerFormGroup'] && !changes['triggerFormGroup'].firstChange) {
      this.checkTrigger();
    }
  }

  ngOnDestroy() {
    this.formSubscription.unsubscribe();
  }

  subscribeToFormChanges(): Subscription {
    return this.triggerFormGroup.valueChanges.pipe(startWith(this.triggerFormGroup.value)).subscribe(trigger => {
      this.triggerComplete$.next(
        this.tasksService.checkIfTriggerIsComplete(trigger, this.selectedMetric$.value?.type || null)
      );
    });
  }

  checkTrigger() {
    if (this.triggerFormGroup.value.trackerId) {
      this.setSelectedTracker();
    }
  }

  setSelectedTracker() {
    this.trackers$
      .pipe(
        filter(t => !!t),
        take(1)
      )
      .subscribe(trackers => {
        const tracker = trackers.find(t => t._id === this.triggerFormGroup.value.trackerId);
        if (!tracker) {
          return;
        }
        this.selectedTracker$.next(tracker);
        this.setTrackerMetrics(tracker);
        this.setSelectedMetric(tracker);
      });
  }

  setTrackerMetrics(tracker: Tracker) {
    const metrics = tracker.metrics.filter(m => m.updateable && m.type !== TrackerMetricTypes.Photo);

    //TODO: Add date metric back in
    // const metricsWithDate = [...metrics, {...this.dateMetric}];
    this.selectedFilteredTrackerMetrics$.next(metrics);
  }

  setSelectedMetric(tracker: Tracker) {
    const metric =
      this.triggerFormGroup.value.metricId === 'date'
        ? {...this.dateMetric}
        : tracker.metrics.find(m => m._id === this.triggerFormGroup.value.metricId);
    if (!metric) {
      return;
    }
    this.selectedMetric$.next(metric);
  }

  onTracker(option: OphAutocompleteOption) {
    this.triggerFormGroup.patchValue({
      trackerId: option._id,
    });
    this.trackers$.pipe(take(1)).subscribe(trackers => {
      const tracker = trackers[option.index];
      this.selectedTracker$.next(tracker);
      this.setTrackerMetrics(tracker);
    });
  }

  onMetric(metric: TrackerMetric) {
    this.selectedMetric$.next(metric);
    this.triggerFormGroup.patchValue({
      metricId: metric._id,
      value: '',
    });
    if (metric.type !== TrackerMetricTypes.Numeric) {
      this.triggerFormGroup.patchValue({
        operator: 'eq',
      });
    }
  }

  onOperator(operator: OperatorOption) {
    this.triggerFormGroup.patchValue({
      operator: operator.value,
    });
  }

  onDateChange(scheduleMoment: moment.Moment) {
    this.triggerFormGroup.patchValue({
      value: this.timezoneService.convertDateToUtcString(scheduleMoment.toDate()),
    });
  }

  onValue(value: string | Date) {
    // Handle Date objects from the datepicker
    const formValue = value instanceof Date ? value.toISOString() : value;
    this.triggerFormGroup.patchValue({
      value: formValue,
    });
  }

  onRemoveItem() {
    this.selectedMetric$.next(null);
    this.removeItem.emit();
  }
}
